<template>
    <component :is="componentTagType"
               :href="componentTagType === 'a' ? url : null"
               class="menu-button"
               :class="buttonClass"
               @click="handleClick">
        <div class="button-row">
            <i v-if="subtype === ButtonSubtypeEnum.CODE"
               class="fa-solid fa-code" />
            <i v-else-if="subtype === ButtonSubtypeEnum.SUBSCRIBE"
               class="fa-solid fa-envelope" />
            <i v-else
               class="fa-solid fa-list" />
            <div class="button-title">
                {{ title }}
            </div>
        </div>
    </component>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { INavigationButton } from '../types/menuTypes';
    import { ButtonSubtypeEnum, NavigationActionsEnum } from '../enums/menuEnums';
    import { useSubscribeModalStore } from '@/js/store/subscribe/SubscribeModalStore';

    const props = withDefaults(defineProps<INavigationButton>(), {
        subtype: ButtonSubtypeEnum.DEFAULT,
    });

    const subscribeModalStore = useSubscribeModalStore();

    const buttonClass = computed(() => {
        switch (props.subtype) {
        case ButtonSubtypeEnum.DEFAULT:
            return 'default-button';
        case ButtonSubtypeEnum.CODE:
        case ButtonSubtypeEnum.SUBSCRIBE:
            return 'other-button';
        default:
            return 'default-button';
        }
    });

    const componentTagType = computed(() => {
        if (props.subtype === ButtonSubtypeEnum.SUBSCRIBE && props.action) {
            return 'div';
        }

        return 'a';
    });

    const handleClick = () => {
        if (props.subtype === ButtonSubtypeEnum.SUBSCRIBE && props.action) {
            subscribeModalStore.setSubscribeModal(true, props.action === NavigationActionsEnum.NEWSLETTER_MODAL);
        }
    };
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;

.menu-button {
    display: flex;
    min-width: 220px;
    align-items: center;
    justify-content: center;
    padding: 18px 10px;
    border-radius: legacyVariables.$menu-border-radius;
    margin: 6px;
    background: transparent;
    cursor: pointer;
    line-height: 24px;

    &:hover {
        border-color: legacyVariables.$lavender-blue;
        background: legacyVariables.$magnolia;
    }

    &.default-button {
        border: 1px solid legacyVariables.$down-color;
        color: legacyVariables.$down-color;

        .fa-solid {
            background: legacyVariables.$down-color;
        }
    }

    &.other-button {
        border: 1px solid legacyVariables.$purple;
        color: legacyVariables.$purple;

        .fa-solid {
            display: flex;
            width: 27px;
            height: 27px;
            align-items: center;
            justify-content: center;
            background: legacyVariables.$purple;

            &::before {
                margin-top: 1px;
            }

            &.fa-envelope {
                &::before {
                    margin-left: 1px;
                }
            }
        }
    }

    .fa-solid {
        padding: 6px;
        border: 1px solid transparent;
        border-radius: 100%;
        color: legacyVariables.$white;
        font-size: 12px;
    }

    .button-row {
        display: flex;
        height: 27px;
        align-items: center;
        font-size: 14px;
        font-weight: 700;

        i {
            margin-right: 6px;
        }

        .button-title {
            overflow: hidden;
            max-height: 42px;
            line-height: 14px;
            text-align: center;
        }
    }
}

.container-small,
.container-medium,
.container-large {
    .menu-button {
        margin: 0;
    }
}

.dark-theme {
    .menu-button {
        border-color: legacyVariables.$dark-theme-border;
        color: legacyVariables.$white;

        &:hover {
            background: legacyVariables.$dark-theme-medium-purple;
        }

        .fa-solid {
            border: 1px solid legacyVariables.$white;
        }
    }
}
</style>
